import * as z from "zod";
import type { Transaction } from "@rocicorp/zero";
import { userFacingError } from "../authorization";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { hasAccess } from "../rbac/access";
import { mutator } from "./mutator";

export async function isSpaceGitSynced(
  tx: Pick<Transaction<Schema>, "run">,
  spaceId: string
): Promise<boolean> {
  const [remote] = await tx.run(zql.spaceGitRemotes.where("spaceId", spaceId));
  return remote != null;
}

export async function assertNotGitSynced(
  tx: Pick<Transaction<Schema>, "run">,
  spaceId: string
): Promise<void> {
  if (await isSpaceGitSynced(tx, spaceId)) {
    throw userFacingError(
      "This space syncs with a git repository, so live workflows can only change through the remote.",
      { code: "git-synced-space" }
    );
  }
}

export const syncGitSchema = z.object({
  spaceId: z.string(),
  action: z.enum(["push", "pull", "sync"]),
});

// A sync stamp older than this is treated as abandoned (e.g. the task was
// lost to a deploy) so it can’t wedge syncing forever.
export const GIT_SYNC_STALE_MS = 15 * 60_000;

export const syncGit = mutator({
  schema: syncGitSchema,
  resolve: {
    space: { kind: "space", from: "spaceId" },
  },
  permissions: [
    {
      permission: "space:read",
      scopeId: ({ args }) => args.space.id,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    // Syncing is how editors publish drafts to the remote, so it takes either
    // the manage permission (remote configuration) or the push-live permission.
    const canSync =
      (await hasAccess({
        tx,
        userId: ctx.userId,
        principal: ctx.principal,
        scopeId: args.space.id,
        tenantId: ctx.tenantId,
        permission: "space:manage",
      })) ||
      (await hasAccess({
        tx,
        userId: ctx.userId,
        principal: ctx.principal,
        scopeId: args.space.id,
        tenantId: ctx.tenantId,
        permission: "space:push_live",
      }));
    if (!canSync) throw userFacingError("Not found");
    const [remote] = await tx.run(
      zql.spaceGitRemotes.where("spaceId", args.space.id)
    );
    if (!remote) return;
    const now = Date.now();
    if (
      remote.syncStartedAt != null &&
      now - remote.syncStartedAt < GIT_SYNC_STALE_MS
    ) {
      throw userFacingError("A sync is already in progress");
    }
    await tx.mutate.spaceGitRemotes.update({
      spaceId: args.space.id,
      syncStartedAt: now,
      lastError: null,
    });
  },
});
